export const SITE_ORIGIN = 'https://steamplayti.me';

const DEFAULT_META = {
  title: 'Steam Playtime Tracker — Charts, Trends & Game Insights | steamplayti.me',
  description: 'See how many hours you played on Steam each day, game by game. Sign in with Steam once — no install, no password shared. Free daily playtime charts and history.',
  keywords: 'Steam playtime tracker, Steam hours tracker, Steam stats, Steam charts, track Steam playtime, Steam gaming analytics',
  canonical: 'https://steamplayti.me/',
  ogTitle: 'Steam Playtime Tracker — Charts, Trends & Insights',
  ogDescription: 'Visualize your Steam playtime with daily charts, most-played games, and total hours. Privacy-first and lightning fast.',
  ogImage: 'https://steamplayti.me/og.png',
  twitterTitle: 'Steam Playtime Tracker — Charts, Trends & Insights',
  twitterDescription: 'Track your Steam hours with clear, beautiful charts — sign in with Steam once, we never see your password.',
  twitterImage: 'https://steamplayti.me/og.png',
  twitterSite: '@steamplayti_me',
};

function ensureNamedMeta(name) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  return tag;
}

function ensurePropertyMeta(property) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  return tag;
}

function ensureCanonicalLink() {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  return link;
}

function setNamedMeta(name, content) {
  if (typeof document === 'undefined') {
    return;
  }
  const tag = ensureNamedMeta(name);
  tag.setAttribute('content', content);
}

function setPropertyMeta(property, content) {
  if (typeof document === 'undefined') {
    return;
  }
  const tag = ensurePropertyMeta(property);
  tag.setAttribute('content', content);
}

function setCanonical(url) {
  if (typeof document === 'undefined') {
    return;
  }
  const link = ensureCanonicalLink();
  link.setAttribute('href', url);
}

// --- structured data -------------------------------------------------------
//
// The JSON-LD block ships in public/index.html so crawlers see *something*
// before hydration, and every prerendered page inherits that copy. It is
// tagged `data-spt-jsonld` so applyMeta can overwrite it per route: before
// this, every /page/* URL served the homepage's SoftwareApplication graph
// verbatim — each landing page literally declared `"url":"https://steamplayti.me/"`,
// telling Google it was a duplicate of the homepage. Prerender snapshots the
// DOM after mounted() runs, so the per-page graph is what lands in the static
// HTML.

const JSON_LD_ATTR = 'data-spt-jsonld';

function ensureJsonLdTag() {
  let tag = document.querySelector(`script[${JSON_LD_ATTR}]`);
  if (!tag) {
    tag = document.createElement('script');
    tag.setAttribute('type', 'application/ld+json');
    tag.setAttribute(JSON_LD_ATTR, '');
    document.head.appendChild(tag);
  }
  return tag;
}

export function setStructuredData(graph) {
  if (typeof document === 'undefined' || !graph) {
    return;
  }
  ensureJsonLdTag().textContent = JSON.stringify(graph, null, 2);
}

// The two site-identity nodes. Every page embeds these, because a JSON-LD
// `@id` reference only resolves inside the graph it appears in — pointing at
// `https://steamplayti.me/#website` from a landing page does not reach the
// homepage's copy, it just dangles. Repeating the definitions is what Yoast /
// RankMath do, and it keeps SoftwareApplication present on every page rather
// than only the homepage.
function siteNodes() {
  return [
    {
      '@type': 'WebSite',
      '@id': `${SITE_ORIGIN}/#website`,
      url: `${SITE_ORIGIN}/`,
      name: 'steamplayti.me',
      description: DEFAULT_META.description,
      inLanguage: 'en',
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${SITE_ORIGIN}/#app`,
      name: 'steamplayti.me',
      alternateName: 'Steam Playtime Tracker',
      applicationCategory: 'UtilitiesApplication',
      operatingSystem: 'Web',
      url: `${SITE_ORIGIN}/`,
      description: 'Track your Steam playtime with privacy-friendly charts and insights.',
      isAccessibleForFree: true,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    },
  ];
}

function breadcrumbNode(canonical, breadcrumbs) {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonical}#breadcrumbs`,
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.path.startsWith('http') ? crumb.path : `${SITE_ORIGIN}${crumb.path}`,
    })),
  };
}

// The site identity on its own — used by the homepage and as the fallback.
export function defaultStructuredData() {
  return { '@context': 'https://schema.org', '@graph': siteNodes() };
}

// Per-page graph: site identity + a WebPage bound to it + breadcrumbs.
// `breadcrumbs` is [{ name, path }] from site root down to the current page.
export function pageStructuredData({ name, description, canonical, breadcrumbs = [] }) {
  const graph = siteNodes();

  graph.push({
    '@type': 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name,
    description,
    isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
    about: { '@id': `${SITE_ORIGIN}/#app` },
    inLanguage: 'en',
  });

  if (breadcrumbs.length) {
    graph.push(breadcrumbNode(canonical, breadcrumbs));
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

// Blog posts: site identity + BlogPosting + breadcrumbs.
export function articleStructuredData({
  headline, description, canonical, datePublished, dateModified, breadcrumbs = [],
}) {
  const publisher = { '@type': 'Organization', name: 'steamplayti.me', url: `${SITE_ORIGIN}/` };
  const graph = siteNodes();

  graph.push({
    '@type': 'BlogPosting',
    '@id': `${canonical}#article`,
    headline,
    description,
    url: canonical,
    mainEntityOfPage: canonical,
    datePublished,
    dateModified: dateModified || datePublished,
    author: publisher,
    publisher,
    isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
    inLanguage: 'en',
  });

  if (breadcrumbs.length) {
    graph.push(breadcrumbNode(canonical, breadcrumbs));
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

export function applyMeta(overrides = {}) {
  if (typeof document === 'undefined') {
    return;
  }

  const meta = { ...DEFAULT_META, ...overrides };
  const {
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterSite,
    structuredData,
  } = meta;

  if (title) {
    document.title = title;
  }

  if (description) {
    setNamedMeta('description', description);
  }

  if (keywords) {
    setNamedMeta('keywords', keywords);
  }

  if (canonical) {
    setCanonical(canonical);
  }

  setPropertyMeta('og:title', ogTitle || title || DEFAULT_META.ogTitle);
  setPropertyMeta('og:description', ogDescription || description || DEFAULT_META.ogDescription);
  setPropertyMeta('og:image', ogImage || DEFAULT_META.ogImage);
  setPropertyMeta('og:url', ogUrl || canonical || DEFAULT_META.canonical);

  setNamedMeta('twitter:title', twitterTitle || title || DEFAULT_META.twitterTitle);
  setNamedMeta('twitter:description', twitterDescription || description || DEFAULT_META.twitterDescription);
  setNamedMeta('twitter:image', twitterImage || DEFAULT_META.twitterImage);
  setNamedMeta('twitter:site', twitterSite || DEFAULT_META.twitterSite);

  // A page without its own graph falls back to the site identity rather than
  // keeping whatever the previously-rendered route left behind.
  setStructuredData(structuredData || defaultStructuredData());
}

export { DEFAULT_META };
